import React, { useState, useEffect } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Box,
  SelectChangeEvent,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Sms as SmsIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface SMSNumber {
  number: string;
  type: 'direct' | 'pilot';
  name: string;
  is_default: boolean;
}

const SMSFromSelector: React.FC = () => {
  const { member } = useAuth();
  const [loading, setLoading] = useState(false);
  const [smsNumbers, setSmsNumbers] = useState<SMSNumber[]>([]);
  const [selectedNumber, setSelectedNumber] = useState<string>('');
  const [hasExtension, setHasExtension] = useState(false);

  useEffect(() => {
    console.log('SMSFromSelector - member:', member);
    console.log('SMSFromSelector - extension:', member?.extension);

    if (member?.extension) {
      setHasExtension(true);
      fetchSMSNumbers();
    } else {
      setHasExtension(false);
      setSmsNumbers([]);
      setSelectedNumber('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.extension]);

  const fetchSMSNumbers = async () => {
    if (!member?.extension) return;

    console.log('SMSFromSelector - Fetching SMS numbers for extension:', member.extension);

    try {
      setLoading(true);
      const response = await api.get('/endpoints/sms-numbers');

      console.log('SMSFromSelector - API response:', response.data);

      if (response.data.success && response.data.data) {
        const { numbers, default_number } = response.data.data;

        if (numbers && numbers.length > 0) {
          console.log('SMSFromSelector - Found', numbers.length, 'SMS numbers');
          setSmsNumbers(numbers);
          setSelectedNumber(default_number || '');
        } else {
          console.log('SMSFromSelector - No SMS numbers available');
          setSmsNumbers([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch SMS numbers:', error);
      setSmsNumbers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    setSelectedNumber(newValue);

    // Update the default SMS number
    try {
      await api.post('/endpoints/sms-numbers', {
        default_number: newValue || null
      });
    } catch (error) {
      console.error('Failed to update default SMS number:', error);
    }
  };

  // Format phone number for display
  const formatPhoneNumber = (number: string) => {
    const cleaned = number.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return number;
  };

  // Don't show if user has no extension
  if (!hasExtension) {
    console.log('SMSFromSelector - Not showing: no extension');
    return null;
  }

  // Don't show if no SMS numbers available
  if (!loading && smsNumbers.length === 0) {
    console.log('SMSFromSelector - Not showing: no SMS numbers and not loading');
    return null;
  }

  console.log('SMSFromSelector - Rendering with', smsNumbers.length, 'numbers');

  return (
    <Box sx={{ width: '100%' }}>
      <FormControl
        fullWidth
        size="small"
        variant="outlined"
        disabled={loading}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'background.paper',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
          },
          '& .MuiSelect-select': {
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          },
        }}
      >
        <InputLabel id="sms-from-label">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SmsIcon sx={{ fontSize: 16 }} />
            SMS From
          </Box>
        </InputLabel>
        <Select
          labelId="sms-from-label"
          id="sms-from-select"
          value={selectedNumber}
          onChange={handleChange}
          label="SMS From"
        >
          {loading && (
            <MenuItem disabled>
              <CircularProgress size={16} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Loading...
              </Typography>
            </MenuItem>
          )}
          {!loading && (
            <MenuItem value="">
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                No default (auto-select)
              </Typography>
            </MenuItem>
          )}
          {!loading && smsNumbers.map((option) => (
            <MenuItem key={option.number} value={option.number}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <SmsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatPhoneNumber(option.number)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {option.name}
                  </Typography>
                </Box>
                {option.type === 'pilot' && (
                  <Chip
                    label="Main"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default SMSFromSelector;